import axios from 'axios';
import type { IdRequestPause } from '../utils/idRequestHistory';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Create axios instance with auth interceptor
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Interfaces
export interface PhotoIdTask {
  id: string;
  clientId: string;
  trackingId: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  processedAt?: string;
  processedBy?: string;
  attachments: Array<{
    filename: string;
    s3Location?: string;
    blobPath?: string;
    blobName?: string;
    contentType?: string;
    type?: string;
    size?: number;
    sizeBytes?: number;
    url?: string;
    secureUrl?: string;
    hasUrl?: boolean;
  }>;
  rejectionReason?: string;
  emailMetadata: {
    from: string;
    subject: string;
    receivedAt: string;
  };
  clientEmail?: string;
  clientName?: string;
  clientDOB?: string;
  associatedClaimCount?: number;
  aiProcessed?: boolean;
  aiConfidence?: number;
  aiNotes?: string;
  aiApproved?: boolean;
  aiRetryCount?: number;
  aiShouldRetry?: boolean;
  aiNextRetryAt?: string;
}

export interface PhotoIdStats {
  clients: {
    total: number;
    verified: number;
    pending: number;
    rejected: number;
    requested: number;
    notRequested: number;
  };
  tasks: {
    pendingReview: number;
  };
  configuration: {
    enabled: boolean;
    delayDays: number;
    maxPerRun: number;
    rateDelay: number;
  };
}

export interface SendIdRequestResult {
  sent: number;
  failed: number;
  errors: Array<{
    clientId: string;
    email?: string;
    error: string;
  }>;
}

export interface BatchHistoricResult extends SendIdRequestResult {
  totalEligible: number;
}

// Photo ID API functions
export const photoIdApi = {
  // Send ID request to specific clients
  async sendIdRequest(clientIds: string[]): Promise<SendIdRequestResult> {
    const response = await axiosInstance.post<SendIdRequestResult>('/ui/photo-id/send-request', { clientIds });
    return response.data;
  },

  // Send batch ID requests to historic clients
  async sendBatchHistoric(batchSize: number = 500): Promise<BatchHistoricResult> {
    const response = await axiosInstance.post<BatchHistoricResult>('/ui/photo-id/send-batch-historic', { batchSize });
    return response.data;
  },

  // Get verification tasks
  async getVerificationTasks(status: 'pending' | 'approved' | 'rejected' = 'pending', limit: number = 50): Promise<{ tasks: PhotoIdTask[]; count: number }> {
    const params = new URLSearchParams({
      status,
      limit: limit.toString(),
    });
    const response = await axiosInstance.get<{ tasks: PhotoIdTask[]; count: number }>(`/ui/photo-id/tasks?${params}`);
    return response.data;
  },

  // Get verification tasks with pagination
  async getVerificationTasksPaginated(status: 'pending' | 'approved' | 'rejected' = 'pending', limit: number = 50, offset: number = 0): Promise<{ tasks: PhotoIdTask[]; count: number }> {
    const params = new URLSearchParams({
      status,
      limit: limit.toString(),
      offset: offset.toString(),
    });
    const response = await axiosInstance.get<{ tasks: PhotoIdTask[]; count: number }>(`/ui/photo-id/tasks?${params}`);
    return response.data;
  },

  // Approve ID verification
  async approveVerification(taskId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(`/ui/photo-id/tasks/${taskId}/approve`);
    return response.data;
  },

  // Reject ID verification
  async rejectVerification(taskId: string, reason: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(`/ui/photo-id/tasks/${taskId}/reject`, { reason });
    return response.data;
  },

  // Mark ID verification as duplicate (complete without updating client status)
  async markAsDuplicate(taskId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(`/ui/photo-id/tasks/${taskId}/duplicate`);
    return response.data;
  },

  // Reset AI processing for a task
  async resetAiProcessing(taskId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(`/ui/photo-id/tasks/${taskId}/reset-ai`);
    return response.data;
  },

  // Get task attachments with SAS URLs
  async getTaskAttachments(taskId: string): Promise<{ taskId: string; attachments: PhotoIdTask['attachments'] }> {
    const response = await axiosInstance.get<{ taskId: string; attachments: PhotoIdTask['attachments'] }>(`/ui/photo-id/tasks/${taskId}/attachments`);
    return response.data;
  },

  // Get ID verification statistics
  async getStats(): Promise<PhotoIdStats> {
    const response = await axiosInstance.get<PhotoIdStats>('/ui/photo-id/stats');
    return response.data;
  },

  // Get client verification details
  async getClientVerification(clientId: string): Promise<{
    clientId: string;
    verificationStatus?: 'pending' | 'verified' | 'rejected' | null;
    // Verified only through another client record for the same person
    verifiedOnAnotherRecord?: boolean;
    idRequestSent: boolean;
    idRequestSentDate?: string;
    // ID-request history for the claim panel (#659). idRequestReminderCount
    // counts every ID email sent, the first one included; a paused client's
    // idRequestLastSentDate is a 2099 marker, the real one is in idRequestPause.
    idRequestLastSentDate?: string | null;
    idRequestReminderCount?: number | null;
    idRequestPause?: IdRequestPause | null;
    clientDeceased?: boolean;
    hasVerification: boolean;
    verification?: {
      taskId: string;
      status: string;
      createdAt: string;
      processedAt?: string;
      processedBy?: string;
      // How the client became verified ('manual' = staff upload #377) -
      // gates the admin reset-verification button
      method?: string | null;
      attachments: any[];
      attachmentCount: number;
      hasPhotos: boolean;
    };
  }> {
    const response = await axiosInstance.get<{
      clientId: string;
      verificationStatus?: 'pending' | 'verified' | 'rejected' | null;
      verifiedOnAnotherRecord?: boolean;
      idRequestSent: boolean;
      idRequestSentDate?: string;
      idRequestLastSentDate?: string | null;
      idRequestReminderCount?: number | null;
      idRequestPause?: IdRequestPause | null;
      clientDeceased?: boolean;
      hasVerification: boolean;
      verification?: {
        taskId: string;
        status: string;
        createdAt: string;
        processedAt?: string;
        processedBy?: string;
        method?: string | null;
        attachments: any[];
        attachmentCount: number;
        hasPhotos: boolean;
      };
    }>(`/ui/photo-id/client/${clientId}/verification`);
    return response.data;
  },

  // Admin reset of a staff manual-upload verification made in error (wrong
  // file attached / wrongly answered "verified"). Deletes the open task and
  // its manual/<clientId>/ blob(s), strips the verification fields from the
  // client record (idRequest* history untouched) and writes a corrective
  // audit record. Sends NO email - deliberately not the reject flow.
  // reason is optional - the audit record gets a default when omitted.
  async resetManualVerification(clientId: string, reason?: string): Promise<{
    success: boolean;
    clientId: string;
    resetTaskId: string | null;
    deletedBlobs: string[];
    fieldsRemoved: string[];
    resetBy: string;
    warnings: string[];
  }> {
    const response = await axiosInstance.post<{
      success: boolean;
      clientId: string;
      resetTaskId: string | null;
      deletedBlobs: string[];
      fieldsRemoved: string[];
      resetBy: string;
      warnings: string[];
    }>(`/ui/photo-id/client/${clientId}/reset-verification`, reason ? { reason } : {});
    return response.data;
  },

  // Staff upload of a client's ID document received by post/email/in person (#377).
  // Replaces the old document-less "Mark ID as Verified" flip - staff always attach
  // the document they received and answer "Is this ID verified?" Yes/No.
  async uploadManualId(
    clientId: string,
    file: File,
    source: 'postal' | 'email' | 'in_person',
    verified: boolean,
    notes?: string
  ): Promise<{
    success: boolean;
    clientId: string;
    taskId: string;
    status: 'approved' | 'pending';
    verified: boolean;
    source: string;
    uploadedBy: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', source);
    formData.append('verified', verified ? 'true' : 'false');
    if (notes) {
      formData.append('notes', notes);
    }
    const response = await axiosInstance.post<{
      success: boolean;
      clientId: string;
      taskId: string;
      status: 'approved' | 'pending';
      verified: boolean;
      source: string;
      uploadedBy: string;
    }>(`/ui/photo-id/client/${clientId}/manual-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Export individual functions for backward compatibility
export const sendIdRequest = photoIdApi.sendIdRequest;
export const getPendingVerifications = async () => {
  const result = await photoIdApi.getVerificationTasks('pending');
  return result.tasks;
};
export const approveVerification = photoIdApi.approveVerification;
export const rejectVerification = photoIdApi.rejectVerification;